import * as yup from "yup";
import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useCreateUnteraufgabe, useUpdateUnteraufgabe, useCreateErwartungswert, useUpdateErwartungswert, useDeleteErwartungswert, useSearchErwartungswerte} from "../../client/hooks";
import {useCallback, useMemo, useEffect, useState} from "react";
import {Aufgabe, Unteraufgabe, UnteraufgabeDaten, Erwartungswert, ErwartungswertDaten} from "../../api";
import {useUnteraufgabenHandling} from "./useUnteraufgabenHandling";
import {useSnackbar} from "notistack";
import {useNavigate} from "react-router-dom";
import {useQueryClient} from "react-query";
import {DEFAULT_LANGUAGE} from "../../config/language";
import {ErwartungswertFormData} from "../../components/unteraufgaben/ErwartungswerteFields";

export function useUnteraufgabenForm(aufgabe: Aufgabe, unteraufgaben: Unteraufgabe[]): {
    formikConfig: FormikConfig<{ unteraufgaben: Unteraufgabe[] }>;
    isLoadingErwartungswerte: boolean;
} {
    const {translation} = useUnteraufgabenHandling()
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const createUnteraufgabe = useCreateUnteraufgabe();
    const updateUnteraufgabe = useUpdateUnteraufgabe();
    const searchErwartungswerte = useSearchErwartungswerte();
    const createErwartungswert = useCreateErwartungswert();
    const updateErwartungswert = useUpdateErwartungswert();
    const deleteErwartungswert = useDeleteErwartungswert();

    const [erwartungswerteMap, setErwartungswerteMap] = useState<Record<string, Erwartungswert[]>>({});
    const [isLoadingErwartungswerte, setIsLoadingErwartungswerte] = useState(true);

    useEffect(() => {
        const loadErwartungswerte = async () => {
            setIsLoadingErwartungswerte(true);
            const map: Record<string, Erwartungswert[]> = {};
            for (const unteraufgabe of unteraufgaben) {
                if (!unteraufgabe.id.startsWith('neu-')) {
                    try {
                        const result = await searchErwartungswerte(aufgabe.id, unteraufgabe.id);
                        map[unteraufgabe.id] = result.items || [];
                    } catch (error) {
                        map[unteraufgabe.id] = [];
                    }
                } else {
                    map[unteraufgabe.id] = [];
                }
            }
            setErwartungswerteMap(map);
            setIsLoadingErwartungswerte(false);
        };
        loadErwartungswerte();
    }, [unteraufgaben, aufgabe.id, searchErwartungswerte]);
    const unteraufgabeSchema = yup.object().shape({
        titel: yup.string().required(capitalize(translation('sub-task-attributes-title-required-message'))),
        beschreibung: yup.string().nullable(),
        reihenfolge: yup.number().required(capitalize(translation('sub-task-attributes-order-required-message'))),
        prompt: yup.string().required(capitalize(translation('sub-task-attributes-prompt-required-message'))),
        foreignTitel: yup.string().nullable(),
        sprache: yup.string().nullable(),
        foreignPrompt: yup
            .string()
            .when('sprache', {
                is: (lang: string | null | undefined) =>
                    (lang ?? DEFAULT_LANGUAGE) !== DEFAULT_LANGUAGE,
                then: s =>
                    s.transform(value => (typeof value === 'string' ? value.trim() : value))
                        .min(1, translation('sub-task-attributes-prompt-required-message'))
                        .required(translation('sub-task-attributes-prompt-required-message')),
                otherwise: s => s.notRequired(),
            }),
        antworttyp: yup.string().required(capitalize(translation('sub-task-attributes-answer-type-required-message'))),
        gewichtung: yup.number().required(capitalize(translation('task-attributes-weighting-required-message'))),
        pflichtfeld: yup.boolean(),
        nurAuswertung: yup.boolean(),
        noRescheduleOnResponseValue: yup.boolean().nullable(),
    });

    const schema = yup.object().shape({
        unteraufgaben: yup.array().of(unteraufgabeSchema)
    });

    const initialTouched = useMemo(() => ({
        unteraufgaben: unteraufgaben.map(() => ({
            titel: false,
            beschreibung: false,
            reihenfolge: false,
            sprache: false,
            prompt: false,
            foreignTitel: false,
            foreignPrompt: false,
            antworttyp: false,
            gewichtung: false,
            pflichtfeld: false,
            nurAuswertung: false,
            noRescheduleOnResponseValue: false,
        })),
    }), [unteraufgaben]);

    const initialValues = useMemo(() => ({
        unteraufgaben: unteraufgaben.map((unteraufgabe: Unteraufgabe) => {
            const erwartungswerte = erwartungswerteMap[unteraufgabe.id] || [];
            return {
                id: unteraufgabe.id,
                titel: unteraufgabe.titel ?? '',
                beschreibung: unteraufgabe.beschreibung ?? '',
                reihenfolge: unteraufgabe.reihenfolge ?? 0,
                prompt: unteraufgabe.prompt ?? '',
                sprache: aufgabe.sprache ?? DEFAULT_LANGUAGE,
                foreignTitel: unteraufgabe.foreignTitel ?? '',
                foreignPrompt: unteraufgabe.foreignPrompt ?? '',
                antworttyp: unteraufgabe.antworttyp ?? "TEXT",
                gewichtung: unteraufgabe.gewichtung ?? 0,
                mandantId: aufgabe.mandantId,
                pflichtfeld: unteraufgabe.pflichtfeld ?? false,
                nurAuswertung: unteraufgabe.nurAuswertung ?? false,
                noRescheduleOnResponseValue: unteraufgabe.noRescheduleOnResponseValue ?? null,
                aufgabeId: aufgabe.id,
                permissions: unteraufgabe.permissions,
                created: unteraufgabe.created,
                erwartungswerte: erwartungswerte.map(ew => ({
                    id: ew.id,
                    comparator: ew.comparator,
                    expectedAnswer: ew.expectedAnswer
                })),
            };
        })
    }), [unteraufgaben, aufgabe.mandantId, aufgabe.id, aufgabe.sprache, erwartungswerteMap]);

    const onSubmitForm = useCallback(async (
        daten: { unteraufgaben: any[] },
        {setSubmitting, setStatus}: FormikHelpers<{ unteraufgaben: Unteraufgabe[] }>
    ): Promise<void> => {
        try {
            await Promise.all(
                daten.unteraufgaben.map(async (unteraufgabe: any, index: number) => {
                    // Use the row's own id (already present on every form entry, see
                    // initialValues below) instead of looking it up by index in the
                    // originally loaded `unteraufgaben` list. Drag-and-drop reordering
                    // (see handleDragEnd) only reorders the Formik values, not that
                    // original list, so an index-based lookup would pair the wrong id
                    // with the wrong row's data after reordering.
                    const unteraufgabeId: string | undefined = unteraufgabe.id;
                    const unteraufgabeDaten: UnteraufgabeDaten = {
                        titel: unteraufgabe.titel,
                        beschreibung: unteraufgabe.beschreibung,
                        prompt: unteraufgabe.prompt,
                        foreignTitel: unteraufgabe.foreignTitel,
                        foreignPrompt: unteraufgabe.foreignPrompt,
                        antworttyp: unteraufgabe.antworttyp,
                        gewichtung: unteraufgabe.gewichtung,
                        reihenfolge: index + 1,
                        mandantId: aufgabe.mandantId,
                        aufgabeId: aufgabe.id,
                        pflichtfeld: unteraufgabe.pflichtfeld,
                        nurAuswertung: unteraufgabe.nurAuswertung,
                        noRescheduleOnResponseValue: unteraufgabe.noRescheduleOnResponseValue
                    };

                    let finalUnteraufgabeId = unteraufgabeId;
                    if (!unteraufgabeId || unteraufgabeId?.startsWith("neu")) {
                        const created = await createUnteraufgabe(aufgabe.id, unteraufgabeDaten);
                        finalUnteraufgabeId = created.id;
                    } else {
                        await updateUnteraufgabe(aufgabe.id, unteraufgabeId, unteraufgabeDaten);
                    }

                    // Handle erwartungswerte
                    if (finalUnteraufgabeId && unteraufgabe.erwartungswerte) {
                        const formErwartungswerte: (ErwartungswertFormData & { id?: string })[] = unteraufgabe.erwartungswerte;
                        const existingErwartungswerte = erwartungswerteMap[unteraufgabeId || ''] || [];

                        // Delete removed erwartungswerte
                        for (const existing of existingErwartungswerte) {
                            const stillExists = formErwartungswerte.some(fe => fe.id === existing.id);
                            if (!stillExists) {
                                await deleteErwartungswert(aufgabe.id, finalUnteraufgabeId, existing.id);
                            }
                        }

                        // Create or update erwartungswerte
                        for (const formEw of formErwartungswerte) {
                            const erwartungswertDaten: ErwartungswertDaten = {
                                unteraufgabenId: finalUnteraufgabeId,
                                comparator: formEw.comparator,
                                expectedAnswer: formEw.expectedAnswer
                            };

                            if (formEw.id) {
                                // Update existing
                                await updateErwartungswert(aufgabe.id, finalUnteraufgabeId, formEw.id, erwartungswertDaten);
                            } else {
                                // Create new
                                await createErwartungswert(aufgabe.id, finalUnteraufgabeId, erwartungswertDaten);
                            }
                        }
                    }
                })
            );

            setStatus(undefined);
            enqueueSnackbar(capitalize(translation('update-successful')), {
                variant: "success",
            });
            // Navigate to the list first, then mark the list query stale. The
            // destination page mounts the same query and owns the refresh, so
            // we no longer force a refetch on this page while it is unmounting
            // (that refetch used to get aborted, surfacing a bodyless
            // CancelError). Fire-and-forget: a cancelled invalidation must not
            // fail the (already successful) save.
            navigate(`/aufgaben/${aufgabe.id}/unteraufgaben`);
            void queryClient.invalidateQueries(["unteraufgaben"]).catch(() => undefined);
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation('update-failed')), {
                variant: "error",
            });
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [
        queryClient,
        enqueueSnackbar,
        translation,
        navigate,
        aufgabe.id,
        aufgabe.mandantId,
        updateUnteraufgabe,
        createUnteraufgabe,
        createErwartungswert,
        updateErwartungswert,
        deleteErwartungswert,
        erwartungswerteMap
    ]);

    const formikConfig = useMemo(
        () => ({
            validationSchema: schema,
            validateOnMount: true,
            enableReinitialize: false,
            initialTouched: initialTouched,
            initialValues: initialValues,
            onSubmit: onSubmitForm
        }),
        [schema, initialValues, initialTouched, onSubmitForm]
    );

    return useMemo(
        () => ({
            formikConfig,
            isLoadingErwartungswerte
        }),
        [formikConfig, isLoadingErwartungswerte]
    );
}